import { useRef, useState, type FormEvent } from "react";
import type { CommandInput, FieldDef, FieldType, Kind, Node, Space, StatusDef, Template, ViewType } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { agents, byRank, keyOf, kindsOf, people } from "../lib/select";
import { send } from "../lib/actions";
import { navigate, openItem, spaceHref } from "../lib/nav";
import { toast } from "../lib/toast";
import { Avatar, Avatars, Dialog } from "./primitives";
import { DateInput, InlineInput, MenuButton, optionLabel } from "./pickers";
import { LABEL_COLORS, LabelPicker, createLabel } from "./labels";
import { DISCIPLINE_LABELS } from "@melo/shared/client";
import { Icon } from "./icons";

export function NewItemDialog({ space, kindKey, parentId, onClose }: { space: Space; kindKey?: string; parentId?: string | null; onClose: () => void }) {
  const t = useTables();
  const kinds = kindsOf(t, space.id);
  const [kind, setKind] = useState(kindKey ?? kinds[0]?.key ?? "task");
  const k = kinds.find((x) => x.key === kind);
  const templates = [...t.templates.values()].filter((x) => x.spaceId === space.id && x.kindKey === kind);
  const [templateId, setTemplateId] = useState<string>("");
  const template = templateId ? t.templates.get(templateId) : undefined;
  const [title, setTitle] = useState("");
  const [parent, setParent] = useState<string | null>(parentId ?? null);
  const [status, setStatus] = useState("");
  const [owners, setOwners] = useState<string[]>([]);
  const [reviewer, setReviewer] = useState<string>("");
  const [due, setDue] = useState<string | null>(null);
  const [priority, setPriority] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [body, setBody] = useState("");
  const [another, setAnother] = useState(false);
  const [busy, setBusy] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  const priorityDef = k?.fields.find((f) => f.key === "priority");
  const disciplineDef = k?.fields.find((f) => f.key === "discipline");
  const milestones = [...t.nodes.values()].filter((n) => n.spaceId === space.id && !n.draft && t.kinds.get(n.kindId)?.key === "phase").sort(byRank);
  const members = new Set([...t.members.values()].filter((m) => m.spaceId === space.id).map((m) => m.actorId));
  const folks = [...people(t), ...agents(t)].filter((a) => members.has(a.id) || a.role === "admin" || a.type === "agent");
  const reviewers = folks.filter((a) => a.type === "person" && a.role !== "contractor");
  const parentNode = parent ? t.nodes.get(parent) : undefined;

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const tpl = id ? t.templates.get(id) : undefined;
    if (!tpl) return;
    if (typeof tpl.fields.priority === "string" && !priority) setPriority(tpl.fields.priority);
    if (typeof tpl.fields.discipline === "string" && !discipline) setDiscipline(tpl.fields.discipline);
    if (tpl.labels.length && !labels.length) setLabels(tpl.labels);
    if (tpl.body && !body) setBody(tpl.body);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const fields: Record<string, string> = {};
      if (priority) fields.priority = priority;
      if (discipline) fields.discipline = discipline;
      const res = (await send("node.create", {
        spaceId: space.id,
        nodes: [
          {
            kind,
            title: title.trim(),
            parentId: parent,
            status: status || undefined,
            body: body || undefined,
            dueDate: due,
            fields: Object.keys(fields).length ? fields : undefined,
            labels: labels.length ? labels : undefined,
            template: templateId || undefined,
            assignees: [...owners.map((actorId) => ({ actorId, role: "owner" as const })), ...(reviewer ? [{ actorId: reviewer, role: "reviewer" as const }] : [])],
          },
        ],
      })) as { nodes: Node[] };
      const created = res.nodes[0];
      if (another) {
        toast(`Created ${created ? keyOf(store.t, created) : "the item"}`);
        setTitle("");
        setBody("");
        titleRef.current?.focus();
      } else {
        onClose();
        if (created) openItem(keyOf(store.t, created));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`New in ${space.name}`} onClose={onClose} wide>
      <form className="form create-form" onSubmit={submit}>
        <input ref={titleRef} className="input title-field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" aria-label="Title" autoComplete="off" />
        <div className="seg" role="group" aria-label="Type" style={{ flexWrap: "wrap", justifySelf: "start" }}>
          {kinds.map((x) => (
            <button key={x.id} type="button" aria-pressed={kind === x.key} onClick={() => (setKind(x.key), setTemplateId(""), setStatus(""))}>
              {x.name}
            </button>
          ))}
        </div>
        <div className="create-props">
          {templates.length > 0 && (
            <label className="prop">
              <span className="label">Template</span>
              <select className="select" value={templateId} onChange={(e) => applyTemplate(e.target.value)} aria-label="Template">
                <option value="">None</option>
                {templates.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} ({x.subtasks.length} subtasks)
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="prop">
            <span className="label">Part of</span>
            <MenuButton
              label="Part of"
              className="field-btn"
              searchable
              width={320}
              items={() => [
                { id: "none", label: "None", checked: !parent, run: () => setParent(null) },
                ...milestones.map((m) => ({ id: m.id, label: m.title, icon: <Icon name="flag" />, checked: parent === m.id, run: () => setParent(m.id) })),
                ...[...store.t.nodes.values()]
                  .filter((n) => n.spaceId === space.id && !n.draft && t.kinds.get(n.kindId)?.key !== "phase")
                  .sort(byRank)
                  .slice(0, 300)
                  .map((n) => ({ id: n.id, label: n.title, hint: keyOf(store.t, n), checked: parent === n.id, run: () => setParent(n.id) })),
              ]}
            >
              {parentNode ? parentNode.title : <span className="muted">None</span>}
            </MenuButton>
          </div>
          {k && (
            <label className="prop">
              <span className="label">Status</span>
              <select className="select" value={status || k.statuses[0]!.key} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                {k.statuses.map((st) => (
                  <option key={st.key} value={st.key}>
                    {st.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="prop">
            <span className="label">Owners</span>
            <MenuButton
              label="Owners"
              className="field-btn"
              searchable
              multi
              items={() => folks.map((a) => ({ id: a.id, label: a.type === "agent" ? `${a.name} (agent)` : `${a.name}${a.disciplines.length ? ` · ${a.disciplines.map((d) => DISCIPLINE_LABELS[d] ?? d).join(", ")}` : ""}`, icon: <Avatar actor={a} size="sm" />, checked: owners.includes(a.id), run: () => setOwners((o) => (o.includes(a.id) ? o.filter((x) => x !== a.id) : [...o, a.id])) }))}
            >
              {owners.length ? <Avatars actors={owners.map((id) => t.actors.get(id))} max={4} /> : <span className="muted">Unassigned</span>}
            </MenuButton>
          </div>
          <div className="prop">
            <span className="label">Reviewer</span>
            <MenuButton
              label="Reviewer"
              className="field-btn"
              searchable
              items={() => [
                { id: "none", label: "No reviewer", checked: !reviewer, run: () => setReviewer("") },
                ...reviewers.map((a) => ({ id: a.id, label: `${a.name}${a.disciplines.includes("qa") ? " · QA" : ""}`, icon: <Avatar actor={a} size="sm" />, checked: reviewer === a.id, run: () => setReviewer(a.id) })),
              ]}
            >
              {reviewer ? <Avatars actors={[t.actors.get(reviewer)]} /> : <span className="muted">None</span>}
            </MenuButton>
          </div>
          <div className="prop">
            <span className="label">Due</span>
            <DateInput className="field-btn" label="Due" placeholder="No date" value={due} onSave={setDue} />
          </div>
          {priorityDef && (
            <label className="prop">
              <span className="label">Priority</span>
              <select className="select" value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Priority">
                <option value="">None</option>
                {(priorityDef.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {optionLabel(priorityDef, o)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {disciplineDef && (
            <label className="prop">
              <span className="label">Discipline</span>
              <select className="select" value={discipline} onChange={(e) => setDiscipline(e.target.value)} aria-label="Discipline">
                <option value="">None</option>
                {(disciplineDef.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {optionLabel(disciplineDef, o)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="prop">
            <span className="label">Labels</span>
            <LabelPicker spaceId={space.id} value={labels} onChange={setLabels} className="field-btn" />
          </div>
        </div>
        <textarea className="textarea" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Description. Markdown works. What is it, and what does done look like?" aria-label="Description" />
        {template && template.subtasks.length > 0 && (
          <div className="hint">
            Adds {template.subtasks.length} subtasks: {template.subtasks.map((x) => x.title).join(", ")}
            {due ? ", dated back from the due date." : "."}
          </div>
        )}
        <div className="form-actions">
          <label className="check grow">
            <input type="checkbox" checked={another} onChange={(e) => setAnother(e.target.checked)} />
            Create another
          </label>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={busy || !title.trim()}>
            Create
          </button>
        </div>
      </form>
    </Dialog>
  );
}

const VIEW_TYPES: { type: ViewType; label: string; hint: string }[] = [
  { type: "list", label: "List", hint: "Grouped rows, by milestone, status or person" },
  { type: "board", label: "Board", hint: "Columns you drag cards between" },
  { type: "calendar", label: "Calendar", hint: "Due dates on a month; drag to reschedule" },
  { type: "table", label: "Table", hint: "Rows with every field, edited in place" },
  { type: "gallery", label: "Gallery", hint: "Cards with cover images, for concepts" },
  { type: "timeline", label: "Timeline", hint: "Bars on a calendar, for phases and releases" },
  { type: "refs", label: "References", hint: "The space's image and file board" },
];

export function NewViewDialog({ space, onClose }: { space: Space; onClose: () => void }) {
  const t = useTables();
  const kinds = kindsOf(t, space.id);
  const [name, setName] = useState("");
  const [type, setType] = useState<ViewType>("list");
  const [kindKeys, setKindKeys] = useState<string[]>(["task"]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const id = crypto.randomUUID();
    await send("view.upsert", { id, spaceId: space.id, name: name.trim() || VIEW_TYPES.find((v) => v.type === type)!.label, type, config: { kindKeys: type === "refs" ? [] : kindKeys, groupBy: type === "board" ? "status" : type === "list" ? "parent" : "none" } });
    onClose();
    navigate(spaceHref(space.slug, id));
  };
  return (
    <Dialog title="New view" onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <div className="field-row">
          <label className="label" htmlFor="nv-name">Name</label>
          <input id="nv-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="For example: 0.6 content board" />
        </div>
        <div className="choice-list" role="radiogroup" aria-label="Type">
          {VIEW_TYPES.map((v) => (
            <label key={v.type} className={`choice${type === v.type ? " on" : ""}`}>
              <input type="radio" name="nv-type" checked={type === v.type} onChange={() => setType(v.type)} />
              <b>{v.label}</b>
              <span className="muted">{v.hint}</span>
            </label>
          ))}
        </div>
        {type !== "refs" && (
          <div className="field-row">
            <span className="label">Shows</span>
            <div className="row-gap" style={{ flexWrap: "wrap" }}>
              {kinds.map((k) => (
                <label key={k.id} className="check">
                  <input type="checkbox" checked={kindKeys.includes(k.key)} onChange={(e) => setKindKeys(e.target.checked ? [...kindKeys, k.key] : kindKeys.filter((x) => x !== k.key))} />
                  {k.name}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri">
            Create view
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export const STAGES = [
  { key: "development" as const, label: "In development", hint: "Works toward milestones (vertical slice, alpha, beta, launch) in sprints." },
  { key: "live" as const, label: "Live game", hint: "Ships an update every week or month, plus events. Work is planned per update." },
];

export function NewSpaceDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [review, setReview] = useState(true);
  const [stage, setStage] = useState<"live" | "development">("development");
  const [busy, setBusy] = useState(false);
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  const autoPrefix =
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 3) || "";
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await send("space.create", { name: name.trim(), slug, prefix: (prefix || autoPrefix).toUpperCase(), agentReview: review, stage });
      onClose();
      navigate(spaceHref(slug));
    } catch {
      setBusy(false);
    }
  };
  return (
    <Dialog title="New project" onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <div className="field-row">
          <label className="label" htmlFor="ns-name">Name</label>
          <input id="ns-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bannerward TD" />
        </div>
        <div className="field-row">
          <label className="label" htmlFor="ns-prefix">Key prefix</label>
          <input id="ns-prefix" className="input mono" style={{ maxWidth: 140 }} value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))} placeholder={autoPrefix || "BW"} />
          <span className="hint">Items get keys like {(prefix || autoPrefix || "BW") + "-12"}.</span>
        </div>
        <div className="field-row">
          <span className="label">Where is the game?</span>
          <div className="choice-list" role="radiogroup" aria-label="Stage">
            {STAGES.map((st) => (
              <label key={st.key} className={`choice${stage === st.key ? " on" : ""}`}>
                <input type="radio" name="ns-stage" checked={stage === st.key} onChange={() => setStage(st.key)} />
                <b>{st.label}</b>
                <span className="muted">{st.hint}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="check">
          <input type="checkbox" checked={review} onChange={(e) => setReview(e.target.checked)} />
          Review agent work: agent-made items start as drafts, and finished agent work goes to review.
        </label>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={busy || !name.trim() || !slug || slug.length < 2}>
            Create project
          </button>
        </div>
      </form>
    </Dialog>
  );
}

const FIELD_TYPES: { type: FieldType; label: string }[] = [
  { type: "text", label: "Text" },
  { type: "number", label: "Number" },
  { type: "select", label: "One option" },
  { type: "multiselect", label: "Several options" },
  { type: "date", label: "Date" },
  { type: "checkbox", label: "Checkbox" },
  { type: "url", label: "Link" },
  { type: "actor", label: "Person or agent" },
];

const keyify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "f_$1")
    .slice(0, 48) || "field";

/** Project settings: item types, labels and templates. */
export function ProjectSettingsDialog({ space, onClose, tab: initial = "project" }: { space: Space; onClose: () => void; tab?: "project" | "types" | "labels" | "templates" }) {
  const [tab, setTab] = useState(initial);
  return (
    <Dialog title={`${space.name} settings`} onClose={onClose} wide>
      <div className="seg" role="tablist" aria-label="Settings" style={{ marginBottom: 16 }}>
        {(
          [
            ["project", "Project"],
            ["types", "Item types"],
            ["labels", "Labels"],
            ["templates", "Templates"],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} aria-pressed={tab === k} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>
      {tab === "project" && <ProjectForm space={space} />}
      {tab === "types" && <KindsEditor space={space} />}
      {tab === "labels" && <LabelsEditor space={space} />}
      {tab === "templates" && <TemplatesEditor space={space} />}
    </Dialog>
  );
}

function ProjectForm({ space }: { space: Space }) {
  const t = useTables();
  const live = t.spaces.get(space.id) ?? space;
  const save = (patch: Omit<CommandInput<"space.update">, "spaceId">) => void send("space.update", { spaceId: space.id, ...patch });
  return (
    <div className="form">
      <div className="field-row">
        <span className="label">Name</span>
        <InlineInput key={live.name} label="Project name" className="input" value={live.name} onSave={(v) => v && save({ name: v })} />
      </div>
      <div className="field-row">
        <span className="label">Description</span>
        <InlineInput key={live.description} label="Project description" className="input" value={live.description} placeholder="One line about the game" onSave={(v) => save({ description: v })} />
      </div>
      <div className="field-row">
        <span className="label">Where is the game?</span>
        <div className="choice-list" role="radiogroup" aria-label="Stage">
          {STAGES.map((st) => (
            <label key={st.key} className={`choice${live.stage === st.key ? " on" : ""}`}>
              <input type="radio" name="ps-stage" checked={live.stage === st.key} onChange={() => save({ stage: st.key })} />
              <b>{st.label}</b>
              <span className="muted">{st.hint}</span>
            </label>
          ))}
        </div>
        <span className="hint">Launching? Switch to Live game: milestones become updates, and "Plan updates" schedules them weekly or monthly.</span>
      </div>
      <label className="check">
        <input type="checkbox" checked={live.agentReview} onChange={(e) => save({ agentReview: e.target.checked })} />
        Review agent work: agent-made items start as drafts, and finished agent work goes to review.
      </label>
    </div>
  );
}

function LabelsEditor({ space }: { space: Space }) {
  const t = useTables();
  const labels = [...t.labels.values()].filter((l) => l.spaceId === space.id).sort(byRank);
  const [name, setName] = useState("");
  const counts = new Map<string, number>();
  for (const n of t.nodes.values()) for (const l of n.labels) counts.set(l, (counts.get(l) ?? 0) + 1);
  return (
    <div className="form">
      <p className="hint">Labels are shared across the project. Use them for things that cut across milestones: platform, feature area, "needs art".</p>
      <div className="subs">
        {labels.map((l) => (
          <div key={l.id} className="sub">
            <MenuButton
              label={`Color for ${l.name}`}
              className="btn ghost icon sm"
              items={() => LABEL_COLORS.map((c) => ({ id: c, label: c, icon: <span className="label-dot" style={{ background: c }} />, checked: c === l.color, run: () => void send("label.upsert", { id: l.id, spaceId: space.id, name: l.name, color: c }) }))}
            >
              <span className="label-dot" style={{ background: l.color, width: 12, height: 12 }} />
            </MenuButton>
            <InlineInput key={l.name} label={`Rename ${l.name}`} value={l.name} onSave={(v) => v && void send("label.upsert", { id: l.id, spaceId: space.id, name: v, color: l.color })} />
            <span className="muted mono">{counts.get(l.id) ?? 0}</span>
            <button type="button" className="btn ghost icon sm" aria-label={`Delete ${l.name}`} onClick={() => void send("label.remove", { labelId: l.id }).then(() => toast(`Deleted ${l.name}`))}>
              <Icon name="x" />
            </button>
          </div>
        ))}
        {!labels.length && <div className="sub muted">No labels yet.</div>}
      </div>
      <form
        className="row-gap"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          void createLabel(space.id, name).then(() => setName(""));
        }}
      >
        <input className="input" placeholder="New label" value={name} onChange={(e) => setName(e.target.value)} aria-label="New label" />
        <button type="submit" className="btn" disabled={!name.trim()}>
          Add
        </button>
      </form>
    </div>
  );
}

function TemplatesEditor({ space }: { space: Space }) {
  const t = useTables();
  const templates = [...t.templates.values()].filter((x) => x.spaceId === space.id).sort((a, b) => a.name.localeCompare(b.name));
  const [editing, setEditing] = useState<string | "new" | null>(templates[0]?.id ?? null);
  const current = editing && editing !== "new" ? t.templates.get(editing) : undefined;
  return (
    <div className="kinds">
      <div className="kinds-list">
        {templates.map((x) => (
          <button key={x.id} type="button" className="nav-i" aria-current={editing === x.id ? "page" : undefined} onClick={() => setEditing(x.id)}>
            {x.name}
            <span className="cnt">{x.subtasks.length}</span>
          </button>
        ))}
        <button type="button" className="nav-i" onClick={() => setEditing("new")}>
          <Icon name="plus" /> New template
        </button>
      </div>
      <div className="kinds-edit">{editing ? <TemplateForm key={current?.id ?? "new"} space={space} template={current} onSaved={(id) => setEditing(id)} /> : <p className="hint">Templates start an item with its fields, labels, notes and subtasks, for example a raid boss with model, rig, animation, VFX and SFX work.</p>}</div>
    </div>
  );
}

function TemplateForm({ space, template, onSaved }: { space: Space; template?: Template; onSaved: (id: string) => void }) {
  const t = useTables();
  const kinds = kindsOf(t, space.id);
  const [name, setName] = useState(template?.name ?? "");
  const [kindKey, setKindKey] = useState(template?.kindKey ?? "task");
  const [priority, setPriority] = useState(String(template?.fields.priority ?? ""));
  const [discipline, setDiscipline] = useState(String(template?.fields.discipline ?? ""));
  const [labels, setLabels] = useState<string[]>(template?.labels ?? []);
  const [body, setBody] = useState(template?.body ?? "");
  const [checks, setChecks] = useState((template?.checklist ?? []).join("\n"));
  const [subs, setSubs] = useState<{ title: string; discipline: string; days: string }[]>(
    template?.subtasks.map((st) => ({ title: st.title, discipline: String(st.fields?.discipline ?? ""), days: st.dueDaysBefore == null ? "" : String(st.dueDaysBefore) })) ?? [],
  );
  const kind = kinds.find((k) => k.key === kindKey);
  const disciplineDef = kind?.fields.find((f) => f.key === "discipline");
  const priorityDef = kind?.fields.find((f) => f.key === "priority");
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const fields: Record<string, string> = {};
    if (priority && priorityDef) fields.priority = priority;
    if (discipline && disciplineDef) fields.discipline = discipline;
    const saved = (await send("template.upsert", {
      id: template?.id,
      spaceId: space.id,
      name: name.trim(),
      kindKey,
      fields,
      body,
      labels,
      subtasks: subs
        .filter((x) => x.title.trim())
        .map((x) => ({ title: x.title.trim(), fields: x.discipline ? { discipline: x.discipline } : undefined, dueDaysBefore: x.days === "" ? null : Number(x.days) })),
      checklist: checks
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean),
    })) as Template;
    toast(`Saved the ${saved.name} template`);
    onSaved(saved.id);
  };
  return (
    <form className="form" onSubmit={save}>
      <div className="create-props">
        <label className="prop">
          <span className="label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Raid boss" />
        </label>
        <label className="prop">
          <span className="label">Type</span>
          <select className="select" value={kindKey} onChange={(e) => setKindKey(e.target.value)}>
            {kinds.map((k) => (
              <option key={k.id} value={k.key}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
        {priorityDef && (
          <label className="prop">
            <span className="label">Priority</span>
            <select className="select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="">None</option>
              {(priorityDef.options ?? []).map((o) => (
                <option key={o} value={o}>
                  {optionLabel(priorityDef, o)}
                </option>
              ))}
            </select>
          </label>
        )}
        {disciplineDef && (
          <label className="prop">
            <span className="label">Discipline</span>
            <select className="select" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
              <option value="">None</option>
              {(disciplineDef.options ?? []).map((o) => (
                <option key={o} value={o}>
                  {optionLabel(disciplineDef, o)}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="prop">
          <span className="label">Labels</span>
          <LabelPicker spaceId={space.id} value={labels} onChange={setLabels} className="field-btn" />
        </div>
      </div>
      <textarea className="textarea" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Notes every item from this template starts with" aria-label="Notes" />
      <label className="field-row">
        <span className="label">Checklist</span>
        <textarea className="textarea" rows={4} value={checks} onChange={(e) => setChecks(e.target.value)} placeholder={"One step per line, for example:\nQA pass on phone and PC\nPatch notes written"} aria-label="Checklist" />
        <span className="hint">Steps ticked off on the item itself. Use subtasks for work someone else owns.</span>
      </label>
      <div className="field-row">
        <span className="label">Subtasks</span>
        <div className="kf-rows">
          {subs.length > 0 && (
            <div className="kf-row sub-tpl kf-head" aria-hidden="true">
              <span>Title</span>
              <span>Discipline</span>
              <span>Days before due</span>
              <span />
            </div>
          )}
          {subs.map((st, i) => (
            <div key={i} className="kf-row sub-tpl">
              <input className="input" aria-label="Subtask title" value={st.title} onChange={(e) => setSubs(subs.map((x, n) => (n === i ? { ...x, title: e.target.value } : x)))} placeholder="Rig" />
              <select className="select" aria-label="Subtask discipline" value={st.discipline} onChange={(e) => setSubs(subs.map((x, n) => (n === i ? { ...x, discipline: e.target.value } : x)))}>
                <option value="">No discipline</option>
                {Object.entries(DISCIPLINE_LABELS).map(([k2, l]) => (
                  <option key={k2} value={k2}>
                    {l}
                  </option>
                ))}
              </select>
              <input className="input" type="number" min={0} aria-label="Days before the item's due date" value={st.days} onChange={(e) => setSubs(subs.map((x, n) => (n === i ? { ...x, days: e.target.value } : x)))} placeholder="Undated" />
              <button type="button" className="btn ghost icon sm" aria-label={`Remove ${st.title || "subtask"}`} onClick={() => setSubs(subs.filter((_, n) => n !== i))}>
                <Icon name="x" />
              </button>
            </div>
          ))}
          <button type="button" className="btn ghost sm" style={{ justifySelf: "start" }} onClick={() => setSubs([...subs, { title: "", discipline: "", days: "" }])}>
            <Icon name="plus" /> Add subtask
          </button>
        </div>
        <span className="hint">"Days before due" dates each subtask back from the item's due date, so the rig is due before the animation that needs it.</span>
      </div>
      <div className="form-actions">
        {template && (
          <button type="button" className="btn ghost danger" onClick={() => void send("template.remove", { templateId: template.id }).then(() => onSaved(""))}>
            Delete
          </button>
        )}
        <span className="grow" />
        <button type="submit" className="btn pri" disabled={!name.trim()}>
          {template ? "Save template" : "Create template"}
        </button>
      </div>
    </form>
  );
}

/** Edit a project's item types: their fields and their status workflow. */
function KindsEditor({ space }: { space: Space }) {
  const t = useTables();
  const kinds = kindsOf(t, space.id);
  const [selected, setSelected] = useState<string>(kinds[0]?.key ?? "");
  const [creating, setCreating] = useState(false);
  const kind = kinds.find((k) => k.key === selected);
  return (
      <div className="kinds">
        <div className="kinds-list">
          {kinds.map((k) => (
            <button key={k.id} type="button" className={`nav-i${k.key === selected && !creating ? " on" : ""}`} aria-current={k.key === selected && !creating ? "page" : undefined} onClick={() => (setSelected(k.key), setCreating(false))}>
              {k.name}
              <span className="cnt">{k.fields.length}</span>
            </button>
          ))}
          <button type="button" className="nav-i" onClick={() => setCreating(true)}>
            <Icon name="plus" /> New type
          </button>
        </div>
        <div className="kinds-edit">
          {creating ? (
            <KindForm key="new" space={space} onSaved={(k) => (setSelected(k), setCreating(false))} />
          ) : kind ? (
            <KindForm key={kind.id + kind.seq} space={space} kind={kind} onSaved={() => toast(`Saved ${kind.name}`)} />
          ) : null}
        </div>
      </div>
  );
}

function KindForm({ space, kind, onSaved }: { space: Space; kind?: Kind; onSaved: (key: string) => void }) {
  const [name, setName] = useState(kind?.name ?? "");
  const [fields, setFields] = useState<(FieldDef & { optionsText?: string })[]>(kind?.fields.map((f) => ({ ...f, optionsText: f.options?.join(", ") })) ?? []);
  const [statuses, setStatuses] = useState<StatusDef[]>(
    kind?.statuses ?? [
      { key: "proposed", label: "Proposed", category: "todo" },
      { key: "accepted", label: "Accepted", category: "active" },
      { key: "done", label: "Done", category: "done" },
    ],
  );
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const key = kind?.key ?? keyify(name);
      await send("kind.upsert", {
        spaceId: space.id,
        key,
        name: name.trim(),
        fields: fields.map(({ optionsText, ...f }) => ({
          ...f,
          key: f.key || keyify(f.label),
          options: f.type === "select" || f.type === "multiselect" ? (optionsText ?? "").split(",").map((s) => s.trim()).filter(Boolean) : undefined,
          unit: f.unit || undefined,
        })),
        statuses: statuses.map((s) => ({ ...s, key: s.key || keyify(s.label) })),
      });
      onSaved(key);
    } finally {
      setBusy(false);
    }
  };
  const patchField = (i: number, p: Partial<FieldDef & { optionsText?: string }>) => setFields(fields.map((f, n) => (n === i ? { ...f, ...p } : f)));
  const patchStatus = (i: number, p: Partial<StatusDef>) => setStatuses(statuses.map((s, n) => (n === i ? { ...s, ...p } : s)));
  return (
    <form className="form" onSubmit={save}>
      <div className="field-row">
        <label className="label" htmlFor="kf-name">Name</label>
        <input id="kf-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tower family" />
        {kind && <span className="hint mono">key: {kind.key}</span>}
      </div>
      <div className="field-row">
        <span className="label">Fields</span>
        <div className="kf-rows">
          {fields.map((f, i) => (
            <div key={i} className="kf-row">
              <input className="input" aria-label="Field label" value={f.label} onChange={(e) => patchField(i, { label: e.target.value, ...(kind?.fields.some((x) => x.key === f.key) ? {} : { key: keyify(e.target.value) }) })} placeholder="Label" />
              <select className="select" aria-label="Field type" value={f.type} onChange={(e) => patchField(i, { type: e.target.value as FieldType })}>
                {FIELD_TYPES.map((ft) => (
                  <option key={ft.type} value={ft.type}>
                    {ft.label}
                  </option>
                ))}
              </select>
              {f.type === "select" || f.type === "multiselect" ? (
                <input className="input" aria-label="Options" value={f.optionsText ?? ""} onChange={(e) => patchField(i, { optionsText: e.target.value })} placeholder="Options, comma separated" />
              ) : f.type === "number" ? (
                <input className="input" aria-label="Unit" value={f.unit ?? ""} onChange={(e) => patchField(i, { unit: e.target.value })} placeholder="Unit, like studs" />
              ) : (
                <span />
              )}
              <button type="button" className="btn ghost icon sm" aria-label={`Remove ${f.label || "field"}`} onClick={() => setFields(fields.filter((_, n) => n !== i))}>
                <Icon name="x" />
              </button>
            </div>
          ))}
          <button type="button" className="btn ghost sm" style={{ justifySelf: "start" }} onClick={() => setFields([...fields, { key: "", label: "", type: "text" }])}>
            <Icon name="plus" /> Add field
          </button>
        </div>
      </div>
      <div className="field-row">
        <span className="label">Statuses, in order</span>
        <div className="kf-rows">
          {statuses.map((s, i) => (
            <div key={i} className="kf-row status">
              <input className="input" aria-label="Status label" value={s.label} onChange={(e) => patchStatus(i, { label: e.target.value, ...(kind?.statuses.some((x) => x.key === s.key) ? {} : { key: keyify(e.target.value) }) })} placeholder="Label" />
              <select className="select" aria-label="Status meaning" value={s.category} onChange={(e) => patchStatus(i, { category: e.target.value as StatusDef["category"] })}>
                <option value="todo">Not started</option>
                <option value="active">In progress</option>
                <option value="review">In review</option>
                <option value="done">Done</option>
              </select>
              <input className="input" aria-label="Work-in-progress limit" type="number" min={1} value={s.wip ?? ""} onChange={(e) => patchStatus(i, { wip: e.target.value ? Number(e.target.value) : undefined })} placeholder="WIP limit" />
              <button type="button" className="btn ghost icon sm" aria-label={`Remove ${s.label || "status"}`} disabled={statuses.length === 1} onClick={() => setStatuses(statuses.filter((_, n) => n !== i))}>
                <Icon name="x" />
              </button>
            </div>
          ))}
          <button type="button" className="btn ghost sm" style={{ justifySelf: "start" }} onClick={() => setStatuses([...statuses, { key: "", label: "", category: "todo" }])}>
            <Icon name="plus" /> Add status
          </button>
        </div>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn pri" disabled={busy || !name.trim()}>
          {kind ? "Save kind" : "Create kind"}
        </button>
      </div>
    </form>
  );
}
